import React, { useCallback, useState, useEffect, useMemo, useRef } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  TextField,
  Chip,
  Stack,
  IconButton,
  Tabs,
  Tab,
  Badge,
} from "@mui/material";
import { Icon } from '@iconify/react';
import plusFill from '@iconify/icons-eva/plus-fill';
import closeFill from '@iconify/icons-eva/close-fill';
import { BaseTable } from "../BaseTable/BaseTable";
import { LoadingButton } from "@mui/lab";
import { useDispatch } from "react-redux";
import { alertSlice } from "src/store/alert/alert.slice";
import enums from "src/util/enums";
import { useMutate, useGetDetail } from "src/hooks/useFetch.hook";
import { sendRenewalNotificationsByLicensesUrl } from "src/react-query/endPoints";
import { bulkNotificationColumns } from "./teams.constants";


const BulkNotificationModal = ({
  open,
  onClose,
  subscriptionData = [],
  teamId,
  preSelectedLicenseIds = null,
}) => {
  const dispatch = useDispatch();
  const {
    actions: { setAlert },
  } = alertSlice;

  const [selectedLicensesForNotification, setSelectedLicensesForNotification] = useState([]);
  const [expiringLicenses, setExpiringLicenses] = useState([]);
  const [selectedEmails, setSelectedEmails] = useState([]);
  const [newEmail, setNewEmail] = useState("");
  const [activeTab, setActiveTab] = useState(0);
  const emailsInitialized = useRef(false);

  const isPreSelectedMode = preSelectedLicenseIds !== null;
  const effectiveLicenseIds = isPreSelectedMode ? preSelectedLicenseIds : selectedLicensesForNotification;

  const { mutate: sendEmailMutate, isLoading: isSendingEmails } = useMutate("team");
  
  const { data: teamUsers, isFetching: isUsersFetching } = useGetDetail(
    "usersByTeam",
    { id: teamId }
  );

  const TabPanel = React.memo(({ children, value, index, ...other }) => (
    <div
      role="tabpanel"
      id={`notification-tabpanel-${index}`}
      aria-labelledby={`notification-tab-${index}`}
      style={{ display: value === index ? 'block' : 'none' }}
      {...other}
    >
      <Box sx={{ p: 3 }}>{children}</Box>
    </div>
  ));

  // Filter expiring licenses when modal opens or subscription data changes
  useEffect(() => {
    if (open && subscriptionData.length > 0) {
      const expiring = subscriptionData
        .filter(subscription => {
          if (!subscription.chargerSubscriptionExpiryDate) return false;
          
          const expiryDate = new Date(subscription.chargerSubscriptionExpiryDate);
          const today = new Date();
          const timeDifference = expiryDate.getTime() - today.getTime();
          const daysDifference = Math.ceil(timeDifference / (1000 * 3600 * 24));
          
          return daysDifference <= 7 && daysDifference >= 0;
        })
        .sort((a, b) => {
          const expiryDateA = new Date(a.chargerSubscriptionExpiryDate);
          const expiryDateB = new Date(b.chargerSubscriptionExpiryDate);
          const today = new Date();
          
          const daysRemainingA = Math.ceil((expiryDateA.getTime() - today.getTime()) / (1000 * 3600 * 24));
          const daysRemainingB = Math.ceil((expiryDateB.getTime() - today.getTime()) / (1000 * 3600 * 24));
          
          return daysRemainingA - daysRemainingB;
        });
      
      setExpiringLicenses(expiring);
      // Don't auto-select licenses - let user choose which ones to send notifications for
    }
  }, [open, subscriptionData]);

  useEffect(() => {
    if (open && teamUsers?.length > 0 && !emailsInitialized.current) {
      const emails = teamUsers
        .filter(user => user.email && user.email.trim() !== "")
        .map(user => user.email);
      setSelectedEmails(emails);
      emailsInitialized.current = true;
    }
  }, [open, teamUsers]);

  useEffect(() => {
    if (!open) {
      setSelectedLicensesForNotification([]);
      setExpiringLicenses([]);
      setSelectedEmails([]);
      setNewEmail("");
      setActiveTab(0);
      emailsInitialized.current = false; // Reset initialization flag
    }
  }, [open]);

  const handleClose = useCallback(() => {
    setSelectedLicensesForNotification([]);
    setExpiringLicenses([]);
    setSelectedEmails([]);
    setNewEmail("");
    setActiveTab(0);
    emailsInitialized.current = false; // Reset initialization flag
    onClose();
  }, [onClose]);

  
  const licenseTabLabel = useMemo(() => (
    <Badge badgeContent={selectedLicensesForNotification.length} color="primary">
      Licenses
    </Badge>
  ), [selectedLicensesForNotification.length]);

  const emailTabLabel = useMemo(() => (
    <Badge badgeContent={selectedEmails.length} color="secondary">
      Email Recipients
    </Badge>
  ), [selectedEmails.length]);

  const columns = useMemo(() => bulkNotificationColumns(), []);

  const handleAddEmail = useCallback(() => {
    const trimmedEmails = newEmail.trim();
    if (!trimmedEmails) return;

    const emailList = trimmedEmails
      .split(/[,;\s]+/)
      .map(email => email.trim())
      .filter(email => email.length > 0);

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const validEmails = [];
    const invalidEmails = [];

    emailList.forEach(email => {
      if (emailRegex.test(email)) {
        if (!selectedEmails.includes(email)) {
          validEmails.push(email);
        }
      } else {
        invalidEmails.push(email);
      }
    });

    if (validEmails.length > 0) {
      setSelectedEmails(prev => [...prev, ...validEmails]);
    }

    if (invalidEmails.length > 0) {
      dispatch(
        setAlert({
          open: true,
          message: `Invalid email format: ${invalidEmails.join(', ')}`,
          severity: enums.SEVERITY.WARNING,
        })
      );
    }

    if (validEmails.length > 0) {
      setNewEmail("");
    }
  }, [newEmail, selectedEmails, dispatch, setAlert]);

  const handleRemoveEmail = useCallback((emailToRemove) => {
    setSelectedEmails(prev => prev.filter(email => email !== emailToRemove));
  }, []);

  const handleEmailInputKeyPress = useCallback((e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddEmail();
    }
  }, [handleAddEmail]);

  const handleSendBulkEmails = useCallback(() => {
    if (effectiveLicenseIds.length === 0) {
      dispatch(
        setAlert({
          open: true,
          message: "Please select at least one license to send notifications.",
          severity: enums.SEVERITY.WARNING,
        })
      );
      return;
    }

    if (selectedEmails.length === 0) {
      dispatch(
        setAlert({
          open: true,
          message: "Please add at least one email address to send notifications.",
          severity: enums.SEVERITY.WARNING,
        })
      );
      return;
    }

    sendEmailMutate(
      {
        url: sendRenewalNotificationsByLicensesUrl(teamId),
        method: "POST",
        body: {
          licensePks: effectiveLicenseIds,
          emails: selectedEmails
        }
      },
      {
        onSuccess: () => {
          dispatch(
            setAlert({
              open: true,
              message: `Bulk notifications sent successfully to ${selectedEmails.length} email(s) for ${effectiveLicenseIds.length} license(s).`,
              severity: enums.SEVERITY.SUCCESS,
            })
          );
          handleClose();
        },
        onError: (error) => {
          console.error("Failed to send bulk emails:", error);
          dispatch(
            setAlert({
              open: true,
              message: "Failed to send bulk notifications. Please try again.",
              severity: enums.SEVERITY.ERROR,
            })
          );
        }
      }
    );
  }, [effectiveLicenseIds, selectedEmails, dispatch, setAlert, sendEmailMutate, handleClose, teamId]);

  const emailRecipientsContent = (
    <Box sx={{ p: 2 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle2" sx={{ mb: 1 }}>
          Add Email Addresses
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
          <TextField
            multiline
            maxRows={3}
            placeholder="Enter email addresses (separated by commas)&#10;e.g., user1@example.com, user2@example.com"
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            onKeyDown={handleEmailInputKeyPress}
            sx={{ flexGrow: 1 }}
            type="email"
          />
          <IconButton
            onClick={handleAddEmail}
            disabled={!newEmail.trim()}
            color="primary"
            sx={{ mt: 1, flexShrink: 0 }}
          >
            <Icon icon={plusFill} />
          </IconButton>
        </Box>
      </Box>

      <Box>
        {selectedEmails.length > 0 ? (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 2 }}>
              Selected Recipients ({selectedEmails.length})
            </Typography>
            <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', gap: 1 }}>
              {selectedEmails.map((email) => (
                <Chip
                  key={email}
                  label={email}
                  onDelete={() => handleRemoveEmail(email)}
                  deleteIcon={<Icon icon={closeFill} />}
                  variant="outlined"
                  size="small"
                />
              ))}
            </Stack>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
              No email addresses added yet
            </Typography>
          </Box>
        )}
      </Box>

      {isUsersFetching && (
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          Loading team users...
        </Typography>
      )}
    </Box>
  );

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle>
        <Typography variant="h6">Send Bulk Notifications</Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
          {isPreSelectedMode
            ? `Send expiry notifications for ${effectiveLicenseIds.length} selected license(s)`
            : 'Select licenses to send expiry notifications'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {isPreSelectedMode ? (
          emailRecipientsContent
        ) : (
          <>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}
            >
              <Tab label={licenseTabLabel} />
              <Tab label={emailTabLabel} />
            </Tabs>

            {/* Licenses Tab Content */}
            <Box sx={{ display: activeTab === 0 ? 'block' : 'none' }}>
              <BaseTable
                rows={expiringLicenses ?? []}
                columns={columns}
                getRowId={(row) => row.licensePk}
                loading={false}
                pageSize={10}
                disablePagination={expiringLicenses.length <= 10}
                enableSelection={true}
                onSelectionChange={(selectedRows) => {
                  const selectedIds = selectedRows.map(row => row.licensePk);
                  setSelectedLicensesForNotification(selectedIds);
                }}
              />

              {expiringLicenses.length === 0 && (
                <Typography variant="body1" textAlign="center" sx={{ py: 3 }}>
                  No licenses are expiring within the next 7 days.
                </Typography>
              )}
            </Box>

            {/* Email Recipients Tab Content */}
            <Box sx={{ display: activeTab === 1 ? 'block' : 'none' }}>
              {emailRecipientsContent}
            </Box>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant="outlined">
          Cancel
        </Button>
        <LoadingButton
          onClick={handleSendBulkEmails}
          variant="contained"
          loading={isSendingEmails}
          disabled={effectiveLicenseIds.length === 0 || selectedEmails.length === 0}
        >
          Send Email ({effectiveLicenseIds.length} Licenses, {selectedEmails.length} Emails)
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

export default BulkNotificationModal;